import { useEffect, useMemo, useState } from "react"

import { Col, Container, Row, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import { BehaviorSubject, concat, of } from "rxjs"
import { catchError, distinctUntilChanged, filter, map, switchMap } from "rxjs/operators"

import ImportJobValidationResultGetObservable, {
  ImportJobValidationResult,
  ValidationResultRow,
} from "../../api/Randoms/Import/ImportJobValidationResultGet"
import FormatError from "../../formatters/FormatError"
import LoadingTableWrapper from "../LoadingTableWrapper"
import TablePagination from "../TablePagination"

interface ImportErrorDetailsProps {
  importJobID: number
  errors?: ImportJobValidationResult
  pageSize?: number
}

interface GetErrorsParams {
  importJobID: number
  pageSize?: number
  pageNumber: number
}

interface DataLoader {
  data: ImportJobValidationResult
  loading: boolean
}

function ImportErrorDetails(props: ImportErrorDetailsProps) {
  const { importJobID, pageSize, errors } = props
  const [importErrors, setImportErrors] = useState<DataLoader>({
    data: errors || {
      ImportJobID: importJobID,
      TableHeaderItems: [],
      TableRows: [],
      Pages: 1,
      Total: 0,
      TitleMessage: "",
      CanBeImported: false,
    },
    loading: false,
  })

  const page$ = useMemo(
    () =>
      new BehaviorSubject<GetErrorsParams>({
        importJobID: errors ? -1 : importJobID,
        pageSize,
        pageNumber: 1,
      }),
    [importJobID, pageSize, errors]
  )

  useEffect(() => {
    setImportErrors((prev) => ({ data: { ...prev.data }, loading: page$.value.importJobID > 0 }))

    const pageSubscription = page$
      .pipe(
        filter((v) => v.importJobID > 0),
        distinctUntilChanged(),
        switchMap((params: GetErrorsParams) =>
          concat(
            of({ loading: true }),
            ImportJobValidationResultGetObservable(params.importJobID, params.pageNumber, params.pageSize).pipe(
              map((result: ImportJobValidationResult) => ({ data: result, loading: false })),
              catchError((error) => of({ loading: false, errorMessage: FormatError(error) }))
            )
          )
        )
      )
      .subscribe(
        (o: { loading: boolean; data?: ImportJobValidationResult; errorMessage?: string }) => {
          setImportErrors((prev) => ({ data: { ...prev.data, ...o.data }, loading: o.loading }))
          if (o.errorMessage) {
            toast.error(o.errorMessage)
          }
        },
        (error) => {
          setImportErrors((prev) => ({ data: { ...prev.data }, loading: false }))
          toast.error(FormatError(error))
        }
      )
    return () => pageSubscription.unsubscribe()
  }, [errors, page$])

  const pageChange = (pageNumber: number) => page$.next({ ...page$.value, importJobID, pageNumber })

  const { TitleMessage, TableHeaderItems = [], TableRows = [], Pages = 1 } = importErrors.data

  const renderImportCells = (row: ValidationResultRow) => {
    return row.Cells.map((cell, i) => (
      <td key={`cell_${TableHeaderItems[i]}`} className="text-nowrap">
        {i === 1 ? <div dangerouslySetInnerHTML={{ __html: cell }} /> : <div>{cell}</div>}
      </td>
    ))
  }

  const renderImportRows = () => {
    return TableRows.map((row) => <tr key={`row_${row.Cells[0]}`}>{renderImportCells(row)}</tr>)
  }

  return (
    <Container fluid className="grid-container p-0">
      {TitleMessage && (
        <Row>
          <Col className="h6">{TitleMessage}</Col>
        </Row>
      )}
      {TableRows.length > 0 && (
        <>
          <Row>
            <Col>
              <LoadingTableWrapper loading={importErrors.loading} variant="light">
                <Table className="table-sm mb-0" responsive bordered size="sm">
                  <thead>
                    <tr>
                      {TableHeaderItems.map((item) => (
                        <th key={`h_${item}`}>{item}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{renderImportRows()}</tbody>
                </Table>
              </LoadingTableWrapper>
            </Col>
          </Row>
          <Row>
            <Col>
              <TablePagination
                pageNumber={page$.value.pageNumber}
                pages={Pages}
                className="mt-3 mb-0"
                onChange={pageChange}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}

ImportErrorDetails.defaultProps = {
  errors: undefined,
  pageSize: 10,
}

export default ImportErrorDetails
